import { AlertTriangle } from "lucide-react";

const CRIT_MAP = {
  routine: { bg: "bg-emerald-50", bd: "border-emerald-200", fg: "text-emerald-700", icon: "🟢", txt: "Routine" },
  monitor: { bg: "bg-blue-50", bd: "border-blue-200", fg: "text-blue-700", icon: "🔵", txt: "Monitor" },
  attention: { bg: "bg-amber-50", bd: "border-amber-200", fg: "text-amber-700", icon: "🟡", txt: "Needs attention" },
  urgent: { bg: "bg-orange-50", bd: "border-orange-200", fg: "text-orange-700", icon: "🟠", txt: "Urgent" },
  emergency: { bg: "bg-red-50", bd: "border-red-200", fg: "text-red-700", icon: "🔴", txt: "Emergency" },
};

const URGENCY_LABEL = { now: "Now", within_days: "Within days", within_weeks: "Within weeks", routine: "Routine" };

function Section({ icon, title, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base">{icon}</span>
        <h3 className="text-[15px] font-bold text-gray-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start mb-1.5">
      <span className="text-sky-500 mr-2 leading-6">•</span>
      <span className="flex-1 text-sm text-slate-700 leading-6">{children}</span>
    </div>
  );
}

const flagColor = (status) => {
  const st = String(status || "").toLowerCase();
  if (st === "critical") return "bg-red-700";
  if (["abnormal", "high", "low"].includes(st)) return "bg-orange-600";
  if (st === "borderline") return "bg-amber-600";
  return "bg-emerald-600";
};

export default function HealthAnalysisView({ ai, summaryFallback }) {
  if (!ai && !summaryFallback) return null;
  const a = ai || {};

  return (
    <div data-testid="health-analysis-view" className="text-left">
      {/* Badges */}
      {(a.specialty || a.scanType || a.pagesAnalyzed > 1) && (
        <div className="flex flex-wrap gap-2 mb-3">
          {a.specialty && <span className="text-xs font-bold text-violet-700 bg-violet-100 px-3 py-1.5 rounded-full">🩺 {a.specialty}</span>}
          {a.scanType && <span className="text-xs font-bold text-sky-700 bg-sky-100 px-3 py-1.5 rounded-full">🖼️ {a.scanType}</span>}
          {a.pagesAnalyzed > 1 && (
            <span className="text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full">
              📄 {a.pagesTruncated ? `Read ${a.pagesAnalyzed} of ${a.totalPages} pages` : `${a.pagesAnalyzed} pages read`}
            </span>
          )}
        </div>
      )}

      {/* Truncation notice */}
      {a.pagesTruncated && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-3">
          <p className="text-[13px] text-orange-800 font-semibold leading-5">
            ⚠️ This is a large document ({a.totalPages} pages). AI reviewed only the first {a.pagesAnalyzed} pages. Please share the complete report with your doctor for a full review.
          </p>
        </div>
      )}

      {/* Criticality banner */}
      {a.criticality?.level && (() => {
        const cfg = CRIT_MAP[String(a.criticality.level).toLowerCase()] || CRIT_MAP.attention;
        return (
          <div className={`rounded-xl border ${cfg.bg} ${cfg.bd} p-3.5 mb-3`}>
            <div className="flex items-center gap-2">
              <span className="text-base">{cfg.icon}</span>
              <span className={`flex-1 text-[15px] font-extrabold ${cfg.fg}`}>{a.criticality.label || cfg.txt}</span>
              <span className={`border ${cfg.bd} rounded-xl px-2.5 py-0.5 text-[11px] font-bold ${cfg.fg}`}>{cfg.txt}</span>
            </div>
            {a.criticality.explanation && <p className="text-[13px] text-gray-700 mt-2 leading-5">{a.criticality.explanation}</p>}
          </div>
        );
      })()}

      {/* Red flags */}
      {a.redFlags?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3.5 mb-3">
          <p className="text-sm font-extrabold text-red-700 mb-1.5">🚑 Red flags</p>
          {a.redFlags.map((rf, i) => (
            <div key={i} className="flex items-start mb-1">
              <span className="text-red-600 mr-2 leading-5">•</span>
              <span className="flex-1 text-[13.5px] text-red-900 leading-5">{rf}</span>
            </div>
          ))}
        </div>
      )}

      {/* What this means */}
      {(a.detailedExplanation || a.summary || summaryFallback) && (
        <Section icon="📝" title="What this means">
          <p className="text-sm text-gray-700 leading-6 whitespace-pre-line">{a.detailedExplanation || a.summary || summaryFallback}</p>
        </Section>
      )}

      {/* Scan interpretation */}
      {a.isScan && (a.imagingFindings?.length > 0 || a.impression) && (
        <Section icon="🔬" title="Scan interpretation">
          {a.imagingFindings?.map((f, i) => <Bullet key={i}>{f}</Bullet>)}
          {a.impression && (
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 mt-2">
              <p className="text-[11px] font-extrabold text-sky-700 uppercase tracking-wide mb-1">Impression</p>
              <p className="text-sm text-sky-900 leading-5">{a.impression}</p>
            </div>
          )}
        </Section>
      )}

      {/* Who to consult */}
      {a.recommendedSpecialist && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
          <p className="text-[13px] font-extrabold text-emerald-700 mb-1">👨‍⚕️ Who to consult</p>
          <p className="text-sm text-emerald-900 leading-5">{a.recommendedSpecialist}</p>
        </div>
      )}

      {/* Possible reasons */}
      {a.possibleReasons?.length > 0 && (
        <Section icon="💡" title="Possible reasons">
          {a.possibleReasons.map((r, i) => <Bullet key={i}>{r}</Bullet>)}
        </Section>
      )}

      {/* Next steps */}
      {a.nextSteps?.length > 0 && (
        <Section icon="✅" title="Next steps">
          {a.nextSteps.map((st, i) => {
            const step = typeof st === "string" ? { step: st } : st;
            return (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-2">
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-sm font-semibold text-gray-800 leading-5">{step.step}</span>
                  {step.urgency && (
                    <span className="bg-indigo-100 text-indigo-700 rounded-lg px-2 py-0.5 text-[10.5px] font-bold">
                      {URGENCY_LABEL[step.urgency] || step.urgency}
                    </span>
                  )}
                </div>
                {step.why && <p className="text-[12.5px] text-slate-500 mt-1 leading-[18px]">{step.why}</p>}
              </div>
            );
          })}
        </Section>
      )}

      {/* Key findings */}
      {a.keyFindings?.length > 0 && (
        <Section icon="🔍" title="Key findings">
          {a.keyFindings.map((f, i) => <Bullet key={i}>{f}</Bullet>)}
        </Section>
      )}

      {/* Flagged values */}
      {a.flaggedValues?.length > 0 && (
        <Section icon="⚠️" title="Flagged values">
          {a.flaggedValues.map((v, i) => (
            <div key={i} className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2">
              <div className="flex items-center justify-between">
                <span className="flex-1 text-sm font-bold text-gray-800">{v.name || v.testName || `Value ${i + 1}`}</span>
                {v.status && <span className={`${flagColor(v.status)} text-white rounded-md px-2 py-0.5 text-[10.5px] font-bold uppercase`}>{v.status}</span>}
              </div>
              <p className="text-[13.5px] text-orange-800 mt-1 font-semibold">
                {v.value}{v.reference || v.referenceRange ? `  (ref: ${v.reference || v.referenceRange})` : ""}
              </p>
              {v.interpretation && <p className="text-[12.5px] text-orange-900 mt-0.5 leading-[18px]">{v.interpretation}</p>}
            </div>
          ))}
        </Section>
      )}

      {/* Medications */}
      {a.medications?.length > 0 && (
        <Section icon="💊" title="Medications">
          {a.medications.map((m, i) => (
            <div key={i} className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-2">
              <p className="text-sm font-bold text-violet-800">{m.name}</p>
              <p className="text-[13px] text-violet-700 mt-0.5">{[m.dosage, m.frequency, m.duration].filter(Boolean).join(" • ")}</p>
              {m.instructions && <p className="text-[12.5px] text-violet-600 mt-0.5 italic">{m.instructions}</p>}
            </div>
          ))}
        </Section>
      )}

      {/* Diagnosis */}
      {a.diagnosis?.length > 0 && (
        <Section icon="🩻" title="Diagnosis">
          {a.diagnosis.map((d, i) => <Bullet key={i}>{d}</Bullet>)}
        </Section>
      )}

      {/* Questions to ask */}
      {a.questionsToAskDoctor?.length > 0 && (
        <Section icon="❓" title="Questions to ask your doctor">
          {a.questionsToAskDoctor.map((q, i) => <Bullet key={i}>{q}</Bullet>)}
        </Section>
      )}

      {/* Recommended lab tests */}
      {a.recommendedLabTests?.length > 0 && (
        <Section icon="🧪" title="Recommended tests">
          {a.recommendedLabTests.map((t, i) => (
            <Bullet key={i}>
              <span className="font-bold">{t.testName || t.name}</span>{t.reason ? ` — ${t.reason}` : ""}
            </Bullet>
          ))}
        </Section>
      )}

      {/* Test results table */}
      {a.testResults?.length > 0 && (
        <Section icon="🧾" title="Test results">
          {a.testResults.map((t, i) => {
            const st = String(t.status || "").toLowerCase();
            const color = st === "critical" ? "text-red-700" : st === "high" || st === "low" ? "text-orange-600" : "text-emerald-600";
            return (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="flex-1 text-[13.5px] text-slate-700 pr-2.5">{t.testName || t.name}</span>
                <div className="text-right">
                  <p className={`text-[13.5px] font-bold ${color}`}>{t.value}</p>
                  {(t.referenceRange || t.reference) && <p className="text-[11px] text-slate-400">ref: {t.referenceRange || t.reference}</p>}
                </div>
              </div>
            );
          })}
        </Section>
      )}

      {/* Recommendations */}
      {a.recommendations?.length > 0 && (
        <Section icon="💡" title="Recommendations">
          {a.recommendations.map((r, i) => (
            <div key={i} className="flex items-start gap-2.5 mb-2">
              <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="flex-1 text-sm text-slate-700 leading-6">{r}</span>
            </div>
          ))}
        </Section>
      )}

      {/* Disclaimer */}
      {a.disclaimer && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-1 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-amber-800 leading-5">{a.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
